import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";
import { forgotPassword } from "@/api/auth";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setErr("");

    try {
      setLoading(true);
      await forgotPassword(email);
      setDone(true);
    } catch (error) {
      setErr(
        error?.response?.data?.detail ||
        "Impossible d’envoyer le lien de réinitialisation."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen aurora-mesh flex items-center justify-center px-6 py-10 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-28 -left-28 w-80 h-80 rounded-full bg-[var(--do-accent-soft)] blur-3xl" />
        <div className="absolute bottom-[-120px] right-[-80px] w-96 h-96 rounded-full bg-[var(--do-accent-soft)] blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-medium text-[var(--do-muted)] hover:text-[var(--do-accent)] mb-5"
        >
          <ArrowLeft size={16} />
          Retour à la connexion
        </Link>

        <div className="rounded-[28px] border border-[var(--do-border)] bg-[var(--do-bg-elev)] backdrop-blur-xl shadow-2xl shadow-[var(--do-shadow-blue)] p-7 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-2xl bg-[var(--do-accent-strong)] text-[var(--do-on-accent)] flex items-center justify-center shadow-lg shadow-[var(--do-shadow-blue)]">
              <Mail size={22} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--do-accent)] font-bold">
                AlmaCRM
              </p>
              <h1 className="text-2xl font-bold text-[var(--do-ink)]">
                Mot de passe oublié
              </h1>
            </div>
          </div>

          {done ? (
            <div className="space-y-5">
              <div className="flex gap-3 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-4 text-sm text-[var(--do-accent)]">
                <CheckCircle2 size={20} className="shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Lien envoyé</p>
                  <p className="mt-1">
                    Si un compte existe avec cette adresse, un e-mail de réinitialisation vient d’être envoyé.
                  </p>
                </div>
              </div>

              <Link
                to="/login"
                className="block w-full text-center rounded-2xl bg-[var(--do-accent-strong)] px-5 py-3.5 text-[var(--do-on-accent)] font-bold shadow-lg shadow-[var(--do-shadow-blue)] hover:bg-[var(--do-accent-strong)] transition"
              >
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-[var(--do-muted)] mb-6 leading-6">
                Indiquez votre adresse e-mail. Vous recevrez un lien temporaire pour créer un nouveau mot de passe.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[var(--do-ink)] mb-2">
                    Adresse e-mail
                  </label>
                  <input
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="vous@entreprise.fr"
                    type="email"
                    autoComplete="email"
                    required
                    className="w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
                  />
                </div>

                {err && (
                  <div className="flex gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm text-[var(--do-accent)]">
                    <AlertCircle size={18} className="shrink-0 mt-0.5" />
                    <span>{err}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-2xl bg-[var(--do-accent-strong)] px-5 py-3.5 text-[var(--do-on-accent)] font-bold shadow-lg shadow-[var(--do-shadow-blue)] hover:bg-[var(--do-accent-strong)] disabled:opacity-60 disabled:cursor-not-allowed transition"
                >
                  {loading ? "Envoi en cours..." : "Recevoir le lien"}
                </button>
              </form>

              <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[var(--do-bg-solid)] border border-[var(--do-border)] px-4 py-3">
                <ShieldCheck size={18} className="text-[var(--do-accent)] shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--do-muted)] leading-5">
                  Pour protéger votre compte, nous ne confirmons jamais publiquement si une adresse e-mail existe.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
